import {
  formatLaneScopeSentence,
  resolveWakeLoopLaneDescriptor,
} from "../config/wakeloop-lane-descriptor";
import { resolveCurrentDaemonExpectedVersion } from "../local-service/lifecycle/current-daemon-version";
import {
  type DaemonStartupPreflightResult,
  runDaemonStartupPreflight,
} from "../local-service/lifecycle/daemon-startup-preflight";
import { resolveRuntimeContext } from "../runtime/context";
import { createPresenter } from "../runtime/presenter/index";
import { createDaemonServiceAlignmentProgressUi } from "../system/local-service-alignment-progress-ui";
import {
  type LocalStateAlignmentReport,
  runLocalStateAlignment,
} from "../system/local-state-alignment";
import { emitTopLevelAgentOverview } from "./top-level-agent-overview";

export async function runRepairLocalState(input: {
  includeDaemonService?: boolean;
  view?: string;
}): Promise<{
  repaired: true;
  report: LocalStateAlignmentReport;
  daemonService: DaemonStartupPreflightResult | null;
}> {
  const runtime = await resolveRuntimeContext({
    view: input.view,
  });
  const presenter = createPresenter(runtime);
  const lane = resolveWakeLoopLaneDescriptor();
  if (runtime.resolvedView === "agent") {
    emitTopLevelAgentOverview({
      presenter,
      command: "repair.local_state",
    });
  }
  const report = await runLocalStateAlignment({
    mode: "repair",
  });
  const daemonService =
    input.includeDaemonService === false
      ? null
      : await runDaemonStartupPreflight({
          expectedVersion: resolveCurrentDaemonExpectedVersion(),
          progress: createDaemonServiceAlignmentProgressUi({
            view: runtime.resolvedView,
          }),
          view: runtime.resolvedView,
        });
  if (runtime.resolvedView === "agent") {
    presenter.data({
      code: "repair.local_state.completed",
      payload: {
        lane,
        report,
        daemonService,
      },
    });
  } else {
    presenter.line({
      code: "repair.local_state.completed",
      text: buildRepairLocalStateSummary({
        daemonService,
        lane,
        report,
      }),
    });
  }
  return {
    repaired: true,
    report,
    daemonService,
  };
}

function buildRepairLocalStateSummary(input: {
  lane: ReturnType<typeof resolveWakeLoopLaneDescriptor>;
  report: LocalStateAlignmentReport;
  daemonService: DaemonStartupPreflightResult | null;
}): string {
  return joinRepairSummaryClauses([
    formatLaneScopeSentence(input.lane),
    describeLocalStateRepairOutcome(input.report),
    describeDaemonStartupPreflightOutcome(input.daemonService),
  ]);
}

function describeLocalStateRepairOutcome(
  report: LocalStateAlignmentReport
): string {
  const localStateChanged = hasAlignmentChanges(report);
  const partiallyBackedUpCorruptFiles =
    report.spaceCache.backedUpCorruptFiles > 0;

  if (report.spaceCache.requiresRepair) {
    if (partiallyBackedUpCorruptFiles) {
      return localStateChanged
        ? "Automatic local WakeLoop data upgrade and cleanup completed, and WakeLoop backed up some corrupt Space cache files, but some Space cache files still need attention."
        : "WakeLoop backed up some corrupt Space cache files, but some Space cache files still need attention.";
    }

    return localStateChanged
      ? "Automatic local WakeLoop data upgrade and cleanup completed, but some corrupt Space cache files still need attention."
      : "Local WakeLoop data still needs attention because some corrupt Space cache files could not be fully backed up.";
  }

  return localStateChanged
    ? "Automatic local WakeLoop data upgrade and cleanup completed."
    : "Local WakeLoop data is up to date.";
}

function describeDaemonStartupPreflightOutcome(
  daemonService: Pick<DaemonStartupPreflightResult, "status"> | null
): string {
  if (daemonService === null) {
    return "";
  }
  const status: DaemonStartupPreflightResult["status"] = daemonService.status;
  switch (status) {
    case "already_aligned":
      return "WakeLoop Service for this lane is already aligned.";
    case "aligned":
    case "auto_healed":
      return "WakeLoop Service for this lane was repaired.";
    case "not_installed":
      return "WakeLoop Service is not installed for this lane.";
    case "blocked":
      return "WakeLoop Service in this lane still needs attention.";
    case "failed":
      return "WakeLoop Service repair in this lane failed.";
    default: {
      const unexpectedStatus: never = status;
      throw new Error(
        `Unexpected daemon startup preflight status: ${String(unexpectedStatus)}`
      );
    }
  }
}

function joinRepairSummaryClauses(clauses: string[]): string {
  return clauses.filter((clause) => clause.length > 0).join("\n");
}

function hasAlignmentChanges(report: LocalStateAlignmentReport): boolean {
  return (
    report.storageVersionUpdated ||
    report.runtimeConfig.migratedLegacyConfig ||
    report.runtimeConfig.removedLegacyConfig ||
    report.selectedWakeLoopProfile.migratedSessionSelections > 0 ||
    report.selectedWakeLoopProfile.removedLegacyDirectory ||
    report.selectedWakeLoopProfile.removedLegacySelectionFile ||
    report.spaceCache.migratedAccountSpaceFiles > 0 ||
    report.spaceCache.removedLegacyAccountSpaceDirs > 0 ||
    report.spaceCache.migratedLegacyProfileFiles > 0 ||
    report.spaceCache.removedLegacyProfilesDir ||
    report.spaceCache.migratedLegacyViews > 0 ||
    report.spaceCache.backedUpCorruptFiles > 0 ||
    report.cleanup.removedTemporaryFiles > 0
  );
}
